import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaExclamationTriangle, FaPause } from 'react-icons/fa';
// import { checkVideoAvailability } from '../utils/videoUtils';
import { getContextSettings, VIDEO_CONFIG } from '../config/videoConfig';

// Vérification améliorée de disponibilité vidéo
const checkVideoAvailabilityLocal = (videoUrl) => {
  return new Promise((resolve) => {
    if (!videoUrl) {
      resolve(false);
      return;
    }

    // Pour les URLs locales, faire une vérification HEAD légère
    if (videoUrl.startsWith('/videos/') || videoUrl.includes('/videos/')) {
      fetch(videoUrl, { method: 'HEAD' })
        .then(response => {
          const isAvailable = response.ok;
          console.log(isAvailable ? '✅' : '❌', 'Vidéo locale:', videoUrl, isAvailable ? 'disponible' : 'indisponible');
          resolve(isAvailable);
        })
        .catch(error => {
          console.warn('⚠️ Erreur vérification vidéo:', videoUrl, error);
          resolve(false);
        });
      return;
    }

    // Pour les URLs externes, test minimal
    resolve(true);
  });
};

const VideoPlayer = ({ 
  src, 
  style = {}, 
  controls = true, 
  autoPlay = false, 
  muted = false, 
  preload = "metadata",
  poster = null,
  onClick = null,
  className = "",
  showPlayButton = false,
  context = "carousel" // 'thumbnail', 'carousel', 'fullscreen'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoAvailable, setIsVideoAvailable] = useState(null);
  const videoRef = useRef(null);

  // Vérifier la disponibilité de la vidéo
  useEffect(() => {
    if (src) {
      checkVideoAvailabilityLocal(src).then(setIsVideoAvailable);
    }
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleError = (e) => {
      setHasError(true);
      setIsLoading(false);
      console.error('❌ Erreur de chargement vidéo:', src, {
        error: e.target?.error,
        networkState: e.target?.networkState,
        readyState: e.target?.readyState
      });
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [src]);

  const handleVideoClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handlePlayButtonClick = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.error('Erreur lors de la lecture:', error);
          setHasError(true);
        });
      }
    }
  };

  // Obtenir les paramètres optimisés selon le contexte
  const contextSettings = getContextSettings(context);
  const finalSettings = {
    controls: controls !== undefined ? controls : contextSettings.controls,
    muted: muted !== undefined ? muted : contextSettings.muted,
    autoPlay: autoPlay !== undefined ? autoPlay : contextSettings.autoPlay,
    // Pour les gros fichiers, utiliser 'none' pour les thumbnails
    preload: context === 'thumbnail' ? 'none' : (preload !== undefined ? preload : contextSettings.preload)
  };

  if (hasError || isVideoAvailable === false) {
    return (
      <div 
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          color: '#6c757d',
          flexDirection: 'column',
          gap: '10px'
        }}
        className={className}
      >
        <FaExclamationTriangle size={24} />
        <span style={{ fontSize: '0.9em', textAlign: 'center' }}>
          {VIDEO_CONFIG.errorMessages.unavailable}
        </span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }} className={className}>
      <video
        ref={videoRef}
        src={src}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          borderRadius: 'inherit'
        }}
        controls={finalSettings.controls}
        autoPlay={finalSettings.autoPlay}
        muted={finalSettings.muted}
        preload={finalSettings.preload}
        poster={poster}
        onClick={handleVideoClick}
        playsInline
        crossOrigin="anonymous"
        allowFullScreen
        webkitAllowFullScreen
        mozAllowFullScreen
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '10px 15px',
          borderRadius: '5px',
          fontSize: '0.9em'
        }}>
          Chargement...
        </div>
      )}

      {/* Custom play button overlay */}
      {showPlayButton && !isPlaying && !isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '15px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onClick={handlePlayButtonClick}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
            e.target.style.transform = 'translate(-50%, -50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
            e.target.style.transform = 'translate(-50%, -50%) scale(1)';
          }}
        >
          {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;